import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CatalogProduct } from '@/mocks/catalog';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';

interface Props {
  product: CatalogProduct | null;
  onClose: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  'Сердце и сосуды': 'ri-heart-pulse-line',
  'Обезболивающие': 'ri-capsule-line',
  'Желудок и кишечник': 'ri-leaf-line',
  'Витамины и БАД': 'ri-sun-line',
  'Простуда и грипп': 'ri-temp-cold-line',
  'Антибиотики': 'ri-test-tube-line',
  'Аллергия': 'ri-lungs-line',
};

const QuickViewModal = ({ product: p, onClose }: Props) => {
  const navigate = useNavigate();
  const { addItem, hasItem, removeItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [qty, setQty] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [visible, setVisible] = useState(false);

  /* Animate in */
  useEffect(() => {
    if (p) {
      setQty(1);
      setImgLoaded(false);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [p]);

  /* Close on Escape */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') handleClose();
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /* Lock body scroll */
  useEffect(() => {
    if (p) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [p]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const handleGoToProduct = () => {
    handleClose();
    setTimeout(() => navigate(`/product/${p!.id}`), 300);
  };

  const handleCart = () => {
    if (!p) return;
    if (hasItem(p.id)) {
      removeItem(p.id);
    } else {
      addItem({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color: p.color, category: p.category });
    }
  };

  const handleFavorite = () => {
    if (!p) return;
    toggleFavorite({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color: p.color, category: p.category, rating: p.rating, discount: p.discount, inStock: p.inStock });
  };

  if (!p) return null;

  const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
  const catIcon = CATEGORY_ICONS[p.category] ?? 'ri-medicine-bottle-line';
  const inCart = hasItem(p.id);
  const inFav = isFavorite(p.id);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{
        background: `rgba(5,5,8,${visible ? '0.85' : '0'})`,
        backdropFilter: visible ? 'blur(8px)' : 'blur(0px)',
        transition: 'background 0.28s ease, backdrop-filter 0.28s ease',
      }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0d0d1a 0%, #0a0a14 100%)',
          border: `1px solid ${p.color}30`,
          boxShadow: `0 0 60px ${p.color}15, 0 40px 80px rgba(0,0,0,0.7)`,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.93) translateY(24px)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.28s cubic-bezier(0.22,1,0.36,1), opacity 0.28s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top accent line */}
        <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }} />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.2)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
        >
          <i className="ri-close-line text-white/60 text-base" />
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Left: image */}
          <div
            className="relative md:w-64 shrink-0 flex items-center justify-center p-8"
            style={{ background: `linear-gradient(135deg, #080810, ${p.color}08)`, minHeight: '260px' }}
          >
            {/* Glow */}
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 w-32 h-16 rounded-full"
              style={{ background: p.color, filter: 'blur(24px)', opacity: 0.25 }}
            />
            {/* Corner accents */}
            <div className="absolute top-3 left-3 w-5 h-5" style={{ borderTop: `1px solid ${p.color}60`, borderLeft: `1px solid ${p.color}60` }} />
            <div className="absolute bottom-3 right-3 w-5 h-5" style={{ borderBottom: `1px solid ${p.color}60`, borderRight: `1px solid ${p.color}60` }} />

            {/* Skeleton while loading */}
            {!imgLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-2xl animate-pulse" style={{ background: `${p.color}10` }} />
              </div>
            )}
            <img
              src={p.image}
              alt={p.name}
              onLoad={() => setImgLoaded(true)}
              className="w-44 h-44 object-contain relative z-10 transition-all duration-500"
              style={{
                filter: `drop-shadow(0 12px 24px ${p.color}60)`,
                opacity: imgLoaded ? 1 : 0,
                transform: imgLoaded ? 'scale(1)' : 'scale(0.9)',
              }}
            />

            {/* Badge */}
            {p.badge && (
              <div
                className="absolute top-4 left-4 px-2.5 py-1 rounded-full font-mono text-[10px] font-bold"
                style={{ background: p.badgeColor, color: '#050508', boxShadow: `0 0 10px ${p.badgeColor}` }}
              >
                {p.badge}
              </div>
            )}

            {/* Scanline */}
            <div className="absolute inset-0 scanline opacity-15 pointer-events-none" />
          </div>

          {/* Right: info */}
          <div className="flex-1 p-6 flex flex-col">
            {/* Category + prescription */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px]"
                style={{ background: `${p.color}12`, color: p.color, border: `1px solid ${p.color}25` }}
              >
                <i className={`${catIcon} text-[10px]`} />
                {p.category}
              </span>
              {p.prescription && (
                <span className="px-2.5 py-1 rounded-full font-mono text-[10px] border" style={{ borderColor: '#f0ff0040', color: '#f0ff00', background: 'rgba(240,255,0,0.06)' }}>
                  <i className="ri-file-list-3-line mr-1" />
                  По рецепту
                </span>
              )}
              {!p.inStock && (
                <span className="px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  Нет в наличии
                </span>
              )}
            </div>

            {/* Name */}
            <h2 className="font-orbitron font-black text-base text-white leading-snug mb-2">{p.name}</h2>

            {/* Description */}
            <p className="font-mono text-xs text-white/45 leading-relaxed mb-4 line-clamp-3">{p.description}</p>

            {/* Specs grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: 'Форма', value: p.form, icon: 'ri-capsule-line' },
                { label: 'Бренд', value: p.brand, icon: 'ri-building-line' },
                { label: 'Страна', value: p.country, icon: 'ri-map-pin-line' },
                { label: 'Вещество', value: p.activeSubstance.split('+')[0].trim(), icon: 'ri-test-tube-line' },
              ].map(spec => (
                <div key={spec.label} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <i className={`${spec.icon} text-xs shrink-0`} style={{ color: p.color }} />
                  <div className="min-w-0">
                    <div className="font-mono text-[9px] text-white/25 leading-none mb-0.5">{spec.label}</div>
                    <div className="font-mono text-[10px] text-white/65 truncate">{spec.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Rating */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <i key={s} className={`text-xs ${s <= Math.round(p.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                ))}
              </div>
              <span className="font-orbitron font-bold text-sm" style={{ color: '#f0ff00' }}>{p.rating}</span>
              <span className="font-mono text-xs text-white/30">{p.reviews.toLocaleString()} отзывов</span>
            </div>

            {/* Price */}
            <div className="flex items-end gap-3 mb-5">
              <span
                className="font-orbitron font-black text-3xl"
                style={{ color: p.color, textShadow: `0 0 16px ${p.color}60` }}
              >
                {p.price.toLocaleString()}₽
              </span>
              {p.oldPrice && (
                <>
                  <span className="font-mono text-sm text-white/25 line-through mb-1">{p.oldPrice.toLocaleString()}₽</span>
                  <span
                    className="px-2 py-0.5 rounded-full font-mono text-xs font-bold mb-1"
                    style={{ background: p.badgeColor, color: '#050508' }}
                  >
                    -{discount}%
                  </span>
                </>
              )}
            </div>

            {/* Qty + Cart */}
            <div className="flex items-center gap-3 mb-3">
              {/* Qty selector */}
              <div
                className="flex items-center rounded-xl overflow-hidden"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <button
                  onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-9 h-9 flex items-center justify-center cursor-pointer transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${p.color}15`; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                >
                  <i className="ri-subtract-line text-white/50 text-sm" />
                </button>
                <span className="w-10 text-center font-orbitron font-bold text-sm text-white">{qty}</span>
                <button
                  onClick={() => setQty(q => q + 1)}
                  className="w-9 h-9 flex items-center justify-center cursor-pointer transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${p.color}15`; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                >
                  <i className="ri-add-line text-white/50 text-sm" />
                </button>
              </div>

              {/* Add to cart */}
              <button
                onClick={handleCart}
                disabled={!p.inStock}
                className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                style={
                  inCart
                    ? { background: p.color, color: '#050508', boxShadow: `0 0 20px ${p.color}50` }
                    : { border: `1px solid ${p.color}`, color: p.color, boxShadow: `0 0 10px ${p.color}20` }
                }
                onMouseEnter={e => { if (!inCart && p.inStock) (e.currentTarget as HTMLElement).style.background = `${p.color}15`; }}
                onMouseLeave={e => { if (!inCart) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <i className={inCart ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                {inCart ? 'В КОРЗИНЕ' : p.inStock ? 'В КОРЗИНУ' : 'НЕТ В НАЛИЧИИ'}
              </button>

              {/* Favorite */}
              <button
                onClick={handleFavorite}
                className="w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200 shrink-0"
                style={{
                  background: inFav ? 'rgba(255,45,120,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${inFav ? 'rgba(255,45,120,0.4)' : 'rgba(255,255,255,0.1)'}`,
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
                onMouseLeave={e => { if (!inFav) (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <i className={inFav ? 'ri-heart-fill' : 'ri-heart-line'} style={{ color: inFav ? '#ff2d78' : 'rgba(255,255,255,0.4)', fontSize: '16px' }} />
              </button>
            </div>

            {/* Go to full page */}
            <button
              onClick={handleGoToProduct}
              className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${p.color}40`; (e.currentTarget as HTMLElement).style.color = p.color; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.45)'; }}
            >
              <i className="ri-external-link-line" />
              Открыть полную страницу товара
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
